import { useEffect, useRef, useState, ReactElement } from 'react';
import { DragDropContext, DragStart, Droppable, DropResult, OnDragEndResponder, OnDragStartResponder } from "react-beautiful-dnd";
import { CookieSetOptions } from 'universal-cookie';

import PresentationCard from '../PresentationCard/PresentationCard';
import Header from '../Header/Header';
import './MeetingSMLRer.css';
import fanfare from "../../assets/fanfare.mp3";
import piano from "../../assets/piano.wav";


interface MeetingSMLRerProps {
  /** Meeting name */
  meetingName: string,
  /** Length of the meeting in seconds */
  meetingLength: number,
  /** Whether or not the app is in debug mode */
  isDebugModeEnabled: boolean,
  /** List of presenters given by database */
  presenters: Presenter[],
  /** Bonus time */
  bonusTime: Presenter,
  /** Current index of active presenter */
  cursor: number,
  /**The previous cursor value */
  prevCursor: number,
  /** Whether or not app is in admin view */
  isAdmin: boolean,
  /** Function for handling what happens for every time unit a presenter goes overtime */
  adjustPresentersTime: Function;
  /** Function for handling meeting reset */
  restartMeeting: () => Promise<void>;
  archiveMeeting: Function;
  clearMeetingAgenda: Function;
  /**Cookies */
  cookies: any;
  setCookie: (name: "user", value: any, options?: CookieSetOptions | undefined) => void;
  writeMeetingChange: (valuesToUpdate: StateUpdateObject) => Promise<boolean>;

  updateLocalState: (valuesToUpdate: StateUpdateObject) => void;
  setPresenters: (presneters: Presenter[]) => void;
  timeBuffer: any;

  elapsedTimeTrigger: boolean;
  meetingEndTime: any;
  meetingStartTime: any;

  calculateBonusTime: (presenters: Presenter[], meetingLength: number, cursor: number) => Presenter;

  isDisplayTimeUnitSeconds: boolean;
  meetingID: string;
  setMeetingID: Function;
  presenterOrder: string[];
  updateStateFromDatabase:  () => Promise<void>;
  handleRemoveAll: Function;
  prepareNextSession: () => Promise<void>;
  viewingSessionId: string | null;
  loadHistoricalSession: (sessionId: string) => Promise<void>;
  loadLiveSession: () => Promise<void>;
  liveSessionId: string;
  meetingEditsEnabled: boolean;
  setLoading: Function;
  setIsAdmin: Function;
}

const playSound = (sound: any): void => {
  try {
    const soundToPlay = new Audio(sound);
    soundToPlay.volume = 0.1;
    soundToPlay.play();
  } catch (err) {
    console.log("Failed to play sound. Error:", err);
  }
}

const useKeyPress = (targetKey: string): boolean => {
  const [keyPressed, setKeyPressed] = useState(false);
  function downHandler({ key }: { key: string }) {
    if (key === targetKey) {
      setKeyPressed(true);
    }
  }

  function upHandler({ key }: { key: string }) {
    if (key === targetKey) {
      setKeyPressed(false);
    }
  }

  useEffect(() => {
    window.addEventListener("keydown", downHandler);
    window.addEventListener("keyup", upHandler);

    return () => {
      window.removeEventListener("keydown", downHandler);
      window.removeEventListener("keyup", upHandler);
    };
  });
  return keyPressed;
}


export const MeetingSMLRer = (props: MeetingSMLRerProps): ReactElement => {
  const {
    isAdmin, setIsAdmin, isDebugModeEnabled, isDisplayTimeUnitSeconds,
    meetingID, setMeetingID, cookies, setCookie, setLoading,
    meetingName, meetingLength, meetingStartTime, meetingEndTime,
    cursor, prevCursor, bonusTime, timeBuffer, elapsedTimeTrigger,
    presenters, setPresenters, presenterOrder,
    archiveMeeting, restartMeeting, clearMeetingAgenda, prepareNextSession,
    updateStateFromDatabase, writeMeetingChange, updateLocalState,
    viewingSessionId, loadHistoricalSession, loadLiveSession, liveSessionId, meetingEditsEnabled,
  } = props;

  const [dragging, setDragging] = useState<boolean>(false);
  const downKeyPress = useKeyPress("ArrowDown");
  const upKeyPress = useKeyPress("ArrowUp");
  const [startMeeting, setStartMeeting] = useState<boolean>(false);
  const startAdvanceHandledRef = useRef(false);
  const startSoundPlayedRef = useRef(false);
  const [isNextButtonPressed, setIsNextButtonPressed] = useState<boolean>(false);
  const [isPrevButtonPressed, setIsPrevButtonPressed] = useState<boolean>(false);

  //KENDRA: errorMsg currently broken, needs repair
  const [errorMsg, setErrorMsg] = useState("")
  const [errorVisible, setErrorVisible] = useState(false)

  const [archiveConfirmation, setArchiveConfirmation] = useState(false)

  let dynamicFontSize = 19;

  /** Live runtime (cursor, timers): not while replaying a saved session or legacy archive URL. */
  const canControlLiveRuntime = (): boolean =>
    !viewingSessionId && !meetingID.includes('Archive#');

  // TIME TICKING =================================================================================



  //This use effect is triggered every second
  useEffect(() => {
    if (cursor === -1 || cursor > presenters.length || !canControlLiveRuntime()) return;

    const timeDenominator = isDebugModeEnabled ? 200 : 1000; // If debug mode, make 5x faster

    if (presenters[cursor] !== undefined) {

      //Calculate current presenter's new elasped time
      let newPresenters: Presenter[] = [...presenters];
      newPresenters[cursor].elapsedTime = calculatePresentersElapsedTime(presenters[cursor], timeDenominator);
      updateLocalState({ presentersUpdateAll: newPresenters });
    } else {

      // We are in the bonus slot
      let newBonusTime = { ...bonusTime };
      newBonusTime.elapsedTime = calculatePresentersElapsedTime(newBonusTime, timeDenominator);
      updateLocalState({ bonusTime: newBonusTime });
    }

  }, [elapsedTimeTrigger, cursor, isDebugModeEnabled, presenters.length]);

  const calculatePresentersElapsedTime = (presenter: Presenter, timeDenominator: number): number => {
    // let currentTime: any = new Date().getTime();
    // let currentPresentersStartTime: any = new Date(presenter.startTime).getTime();
    // // let prevElapsedTime = presenter.dbElapsedTime * (1000/timeDenominator);
    // let prevElapsedTime = presenter.dbElapsedTime;
    // return Math.floor(((currentTime + timeBuffer - currentPresentersStartTime) / timeDenominator) + prevElapsedTime);
    let miliSecondToSec = isDebugModeEnabled ? 5000 : 1000;
    let currentTime: any = new Date().getTime();
    let currentPresentersStartTime: any = new Date(presenter.startTime).getTime();
    if (!presenter.startTime || Number.isNaN(currentPresentersStartTime)) {
      return presenter.elapsedTime ?? 0;
    }
    let prevElapsedTime = presenter.dbElapsedTime * miliSecondToSec;
    return Math.floor(((currentTime + timeBuffer - currentPresentersStartTime) + prevElapsedTime) / timeDenominator);
  }

  const calculatePresenterDBElapsedTime = (presenter: Presenter,): number => {
    if (presenter.startTime === "") return presenter.dbElapsedTime;
    let endOfElapsedTime: any = (presenter.endTime !== "")? new Date(presenter.endTime).getTime() : new Date().getTime();
    let currentPresentersStartTime: any = new Date(presenter.startTime).getTime();
    let currentElapsed = Math.max(endOfElapsedTime + timeBuffer - currentPresentersStartTime, 0) / 1000;
    return Math.floor(presenter.dbElapsedTime + currentElapsed);
  }
  // ==============================================================================================


  // SWITCHING BETWEEN PRESENTERS =================================================================
  useEffect(() => {
    if (cursor === -1) {
      startSoundPlayedRef.current = false;
      startAdvanceHandledRef.current = false;
    }
    if (cursor === 0 && prevCursor === -1 && !startSoundPlayedRef.current) {
      startSoundPlayedRef.current = true;
      playSound(fanfare);
    }
    if (cursor === presenters.length + 1 && prevCursor === presenters.length) {
      playSound(piano);
    }
  }, [cursor, prevCursor])

  const getNextPresenterIndex = (currentCursor: number, currentPresenters: Presenter[]) => {
    for (let i = currentCursor + 1; i < currentPresenters.length; i++) {
      if (currentPresenters[i] && currentPresenters[i].slotType !== "Header") return i;
    }
    return Math.min(currentCursor + 1, currentPresenters.length + 1);
  }

  const getPreviousPresenterIndex = (currentCursor: number, currentPresenters: Presenter[], ) => {
    for (let i = currentCursor - 1; i > -1; i--) {
      if (currentPresenters[i] && currentPresenters[i].slotType !== "Header") return i;
    }
    return Math.max(0, cursor - 1);
  }

  useEffect(() => {
    if (!isAdmin || !canControlLiveRuntime() || !meetingEditsEnabled) return;
    if (!startMeeting && !downKeyPress && !isNextButtonPressed) return;

    if (startMeeting) {
      if (startAdvanceHandledRef.current) {
        setStartMeeting(false);
        setIsNextButtonPressed(false);
        return;
      }
      startAdvanceHandledRef.current = true;
    }

    const now = new Date(new Date().getTime() + timeBuffer);
    const nowIso = now.toISOString();
    let valuesToUpdate: StateUpdateObject = {};

    if ((startMeeting || downKeyPress) && cursor === -1) {
      valuesToUpdate.meetingStartTime = now;
    }

    if ((downKeyPress || isNextButtonPressed || startMeeting) && !dragging && (cursor >= -1 && cursor <= presenters.length)) {

      let newPresenters = [...presenters];
      let nextIndex = getNextPresenterIndex(cursor, presenters);

      if (newPresenters[cursor] !== undefined) {
        newPresenters[cursor].endTime = nowIso;
      }

      if (newPresenters[nextIndex] !== undefined) {
        newPresenters[nextIndex].startTime = nowIso;
        newPresenters[nextIndex].elapsedTime = 0;
      }

      if (nextIndex === presenters.length) {
        valuesToUpdate.bonusTime = { ...bonusTime, startTime: nowIso, elapsedTime: 0 };
      }

      valuesToUpdate = {...valuesToUpdate, presentersUpdateAll: newPresenters, cursor:nextIndex};
    }

    if (Object.keys(valuesToUpdate).length > 0) writeMeetingChange(valuesToUpdate);

    setStartMeeting(false)
    setIsNextButtonPressed(false)

  }, [downKeyPress, startMeeting, isNextButtonPressed])

  useEffect(() => {
    if (!isAdmin || !canControlLiveRuntime() || !meetingEditsEnabled) return;
    if (!(upKeyPress || isPrevButtonPressed)) return;

    if ((cursor >= 0 && cursor <= presenters.length + 1) && !dragging) {

      let nextIndex = getPreviousPresenterIndex(cursor, presenters)

      if (nextIndex === -1) {
        restartMeeting();
        return;
      }

      if (nextIndex === cursor) return;
      
      let newPresenters = [...presenters];

      if (newPresenters[nextIndex] !== undefined) {
        newPresenters[nextIndex].dbElapsedTime = calculatePresenterDBElapsedTime(newPresenters[nextIndex]);
        newPresenters[nextIndex].startTime = new Date(new Date().getTime() + timeBuffer);
        // newPresenters[nextIndex].dbElapsedTime += newPresenters[nextIndex].elapsedTime;
        newPresenters[nextIndex].endTime = "";
        // newPresenters[nextIndex].elapsedTime = newPresenters[nextIndex].dbElapsedTime;
      }

      if (newPresenters[cursor] !== undefined) {
        newPresenters[cursor].dbElapsedTime = calculatePresenterDBElapsedTime(newPresenters[cursor]);
        newPresenters[cursor].startTime = "";
        newPresenters[cursor].endTime = "";
        // newPresenters[cursor].dbElapsedTime += newPresenters[cursor].elapsedTime;
      }

      writeMeetingChange({ cursor: nextIndex, presentersUpdateAll: newPresenters });
    }
    
    setIsPrevButtonPressed(false)
  }, [upKeyPress, isPrevButtonPressed])

  useEffect(() => {
    getPresentationCardHeights(presenters, bonusTime);
  }, [presenters])

  // ==============================================================================================


  // DRAGGING PRESENTERS ==========================================================================

  const onDragStart: OnDragStartResponder = (source: DragStart) => {
    //KENDRA TODO - don't allow bonus time to be dragged
    if (
      meetingEditsEnabled &&
      (isAdmin || cookies.user.includes(presenters[source.source.index].id))
    ) {
      setDragging(true)
    }
  }

  const onDragEnd: OnDragEndResponder = async ({ source, destination }: DropResult) => {
    setDragging(false);

    if (destination === undefined || destination === null) return;
    if (source === undefined || source === null) return;
    if (!viewingSessionId && destination.index < source.index && destination.index <= cursor) return;
    if (destination.index === source.index) return;

    let newPresenterOrder = [...presenterOrder]
    let presenterBeingMoved = presenterOrder[source.index]
    newPresenterOrder.splice(source.index, 1);
    newPresenterOrder.splice(destination.index, 0, presenterBeingMoved)
    writeMeetingChange({presenterOrder:newPresenterOrder});

    // const newList = presenters.filter((_: any, idx: number) => idx !== source.index); //Maybe look into atteching unique id to make sure it is the right person
    // newList.splice(destination.index, 0, presenters[source.index]);
    // writeMeetingChange({ presentersUpdateAll: newList, presentersOrder: getPresenterOrder(newList) });

    // const meetingMetadata: AxiosResponse<MeetingMetaData> | void = await getMeetingMetadata(meetingID).catch((err: Error) => console.log(err))
    // if (meetingMetadata && meetingMetadata.data) {
    //   if (JSON.stringify(meetingMetadata.data.presenterOrder) === JSON.stringify(presenterOrder)) {
    //     writeMeetingChange({ presentersUpdateAll: newList, presentersOrder: getPresenterOrder(newList) });
    //   } else {
    //     setErrorVisible(true)
    //     updateStateFromDatabase()
    //     setErrorMsg("Error: You made a meeting change while out of sync with the database. Please try your change again")
    //     setTimeout(function () {
    //       setErrorVisible(false)
    //     }, 8000)
    //   }
    // }
  }
  // ==============================================================================================

  // SLOT SIZE ====================================================================================

  const MIN_FONT_SIZE = 14;
  const MAX_FONT_SIZE = 50;
  const CARD_SPACING = 4;
  const HEADER_HEIGHT = 100;
  const FONT_TO_CARD_HEIGHT_RATIO = 9/6;
  const MIN_PIXELS_PER_SECOND = 7/60;

  const getPresentationCardHeights = (presenters: Presenter[], bonusTime: Presenter): number[] => {
    let heights: number[] = [];
    const totalArea = window.innerHeight - HEADER_HEIGHT;

    // No presenters, return large font with one large font for bonus time
    if (presenters.length === 0) {
      dynamicFontSize = MAX_FONT_SIZE;
      return [totalArea];
    }


    let totalTimeToDisplay = presenters.map(p => Math.max(p.currentDuration, p.elapsedTime)).reduce((prev, curr) => prev + curr);
    let numberOfPresentations = presenters.length;
    if (bonusTime.currentDuration > 0) {
      totalTimeToDisplay += Math.max(bonusTime.currentDuration, bonusTime.elapsedTime)
      numberOfPresentations += 1;
    }

    dynamicFontSize = MIN_FONT_SIZE;
    let baseHeight = 0 , extraArea = 0, pixelPerSecond = 1;

    while (pixelPerSecond > MIN_PIXELS_PER_SECOND && dynamicFontSize < MAX_FONT_SIZE) {
      dynamicFontSize += .5;
      baseHeight = (dynamicFontSize *FONT_TO_CARD_HEIGHT_RATIO) + CARD_SPACING; 
      extraArea = totalArea - (numberOfPresentations * (baseHeight + CARD_SPACING));
      pixelPerSecond = extraArea/totalTimeToDisplay;
      if (pixelPerSecond < 0) pixelPerSecond = 0;
    }

    heights = presenters.map(item => 
      baseHeight + Math.floor((Math.max(item.currentDuration, item.elapsedTime)) * pixelPerSecond)
    );

    if (bonusTime.currentDuration > 0) {
      heights.push(baseHeight + (Math.max(bonusTime.currentDuration, bonusTime.elapsedTime) * pixelPerSecond))
    }

    
    let boundingBoxWidth = window.innerWidth*.8
    if (window.innerWidth > (80* 10)) {
      boundingBoxWidth = boundingBoxWidth*.25;
    }
    dynamicFontSize = Math.max(shrinkFontSize(boundingBoxWidth, baseHeight, dynamicFontSize), MIN_FONT_SIZE)
    return heights;
  }

  function shrinkFontSize(boundingBoxWidth: number, boundingBoxHeight: number, fontSize: number): number {
    // Create a test element to check text overflow
    let testElement = document.createElement("span");
    testElement.style.fontSize = `${fontSize}px`;
    testElement.style.whiteSpace = "nowrap";
    testElement.innerHTML = "W".repeat(10);
    document.body.appendChild(testElement);

    // Check if the test element overflows the bounding box
    while (testElement.offsetWidth > boundingBoxWidth || testElement.offsetHeight > boundingBoxHeight) {
        fontSize--;
        testElement.style.fontSize = `${fontSize}px`;
    }

    // Remove the test element from the document
    document.body.removeChild(testElement);

    return fontSize;
}


  const getShortCard = () => {
    return (dynamicFontSize) < 16;
  }

    //The width of the name portion of a slot based on the longest name in the presenters list

    // const getSlotNameWidth = (): string => {
    //   const BASE_SLOT_NAME_WIDTH = 10;
    //   const ROW_CHARACTER_LIMIT = 20;
    //   const NUMBER_OF_SLOT_NAME_ICONS = 3;
    //   const fontWidthEst = dynamicFontSize*10/12;
    //   const maxCharCount = Math.max(...presenters.map((p: Presenter) => {
    //     if (p.slotType === 'Presenter' || p.slotType === '' || p.slotType === null) return p.name.length
    //     return 1
    //   })) + NUMBER_OF_SLOT_NAME_ICONS;
    //   return `${BASE_SLOT_NAME_WIDTH + (fontWidthEst * Math.min(maxCharCount, ROW_CHARACTER_LIMIT))}px`;
    // }

    let presenterSlotHeights = getPresentationCardHeights(presenters, bonusTime)

    window.onresize = () => {
      presenterSlotHeights = getPresentationCardHeights(presenters, bonusTime)
    }
  

  // ==============================================================================================

  // ARCHIVING ====================================================================================

  // useEffect(() => {
  //   if (cursor === presenters.length + 1 && prevCursor === presenters.length) {
  //     setArchiveConfirmation(true)
  //     archiveMeeting()
  //   }
  // }, [downKeyPress, isNextButtonPressed])

  // const archiveMeeting = async () => {
  //   const meetingMetadata: AxiosResponse<MeetingMetaData> | any = await getMeetingMetadata(meetingID).catch((err: Error) => console.log(err));
  //   const presenterData: AxiosResponse<Presenter[]> | any = await getMeetingPresenters(meetingID).catch((err: Error) => console.log(err));

  //   createMeeting("Archive#" + meetingID, meetingMetadata.data).then(res => {
  //     for (let i = 0; i < presenterData.data.length; i++) {
  //       updateMeetingPresenter(res, presenters[i])
  //         .catch((err: Error) => console.error(err))
  //     }
  //   })
  //   console.log("Meeting has been archived")
  // }

  // const archiveMeetingNew = async () => {
  //   // 1 get database values
  //   // 2 create an archive meeting
  //   // 3 create duplicate presenters for archive meeting
  //   // 4 delete all presenters from current meeting
  //   // 5 reset meeting

  //   /** Get the current meetings values from the database */
  //   const meetingMetadata: AxiosResponse<MeetingMetaData> | any = await getMeetingMetadata(meetingID).catch((err: Error) => console.log(err));
  //   const presenterData: AxiosResponse<Presenter[]> | any = await getMeetingPresenters(meetingID).catch((err: Error) => console.log(err));

  //   /** Create a new archive meeting */
  //   const archivedMeetingId = await createMeeting("Archive#" + meetingID, meetingMetadata.data);
  //   if (!archivedMeetingId)  return; //KENDRA: ;do something on fail

  //   const createArchivePresenters: Promise<any>[] = [];
  //   const deleteExistingPresenters: Promise<any>[] = [];
  //   for (let i = 0; i < presenterData.data.length; i++) {
  //     createArchivePresenters.push( updateMeetingPresenter( archivedMeetingId, presenterData.data[i]) );
  //     deleteExistingPresenters.push( deleteMeetingPresenter(meetingID, presenterData.data[i]) )
      
  //   }
  //   /** Create new presenters for the archive meeting */
  //   await Promise.all(createArchivePresenters);

  //   /** Delete presenters from live meeting */
  //   await Promise.all(deleteExistingPresenters);

  //   /** Reset Meeting */
  //   handleMeetingReset();
    
  // }
  // ==============================================================================================


  return (
    <div className="wrapper">
      <Header meetingName={meetingName} meetingLength={meetingLength} isDebugModeEnabled={isDebugModeEnabled} meetingEndTime={meetingEndTime}
        cursor={cursor} setIsNextButtonPressed={setIsNextButtonPressed} setIsPrevButtonPressed={setIsPrevButtonPressed}
        setStartMeeting={setStartMeeting} isAdmin={isAdmin} elapsedTimeTrigger={elapsedTimeTrigger}
        writeMeetingChange={writeMeetingChange} restartMeeting={restartMeeting} clearMeetingAgenda={clearMeetingAgenda}
        presenters={presenters}
        updateStateFromDatabase={updateStateFromDatabase} meetingStartTime={meetingStartTime}
        cookies={cookies} setCookie={setCookie} meetingID={meetingID} presenterOrder={presenterOrder}
        errorMsg={errorMsg} setErrorMsg={setErrorMsg} errorVisible={errorVisible} setErrorVisible={setErrorVisible}
        setPresenters={setPresenters} setMEETING_ID={setMeetingID} archiveConfirmation={archiveConfirmation}
        setArchiveConfirmation={setArchiveConfirmation} archiveMeeting={archiveMeeting} isArchived={Boolean(viewingSessionId) || meetingID.includes("Archive#")}
        setLoading={setLoading} setIsAdmin={setIsAdmin} bonusTime={bonusTime}
        prepareNextSession={prepareNextSession} viewingSessionId={viewingSessionId}
        loadHistoricalSession={loadHistoricalSession} loadLiveSession={loadLiveSession} liveSessionId={liveSessionId}
        meetingEditsEnabled={meetingEditsEnabled}/>

      <div className="presenterList">
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <Droppable droppableId='presentersDroppable'>
            {provided => {
              return (
                <ul {...provided.droppableProps} ref={provided.innerRef} id="presenterDroppable">
                  {[...presenters,  bonusTime].filter(x => {return (x.name !== 'Bonus Time' || (bonusTime.currentDuration > 0))})
                  .map((presenter: Presenter | Presenter, presenterIndex) => (
                    <PresentationCard
                      isActive={presenterIndex === cursor}
                      isCompleted={presenterIndex < cursor}
                      key={presenter.id}
                      presenter={presenter}
                      index={presenterIndex}
                      presentationCardHeight={presenterSlotHeights[presenterIndex]}
                      isAdmin={isAdmin}
                      isNarrowScreen={(window.innerWidth < (80* 10))}
                      isShortScreen={getShortCard()}
                      displaySeconds={isDisplayTimeUnitSeconds}
                      isDebugModeEnabled={isDebugModeEnabled}
                      meetingStartTime={meetingStartTime}
                      meetingEndTime={meetingEndTime}
                      presenters={presenters}
                      cursor={cursor}
                      meetingLength={meetingLength}
                      cookies={cookies}
                      dragging={dragging}
                      writeMeetingChange={writeMeetingChange}
                      heartBeatTrigger={elapsedTimeTrigger}
                      dynamicFontSize={dynamicFontSize}
                      isArchived={Boolean(viewingSessionId) || meetingID.includes("Archive#")}
                      isViewingCompletedSession={Boolean(viewingSessionId) || meetingID.includes("Archive#")}
                      meetingEditsEnabled={meetingEditsEnabled}
                    />
                  ))
                  }
                </ul>
              )
            }}
          </Droppable>
        </DragDropContext>
      </div>
    </div>

  )
}

